/**
 * Data Import - Professional Excel upload interface for initial setup and ongoing data management.
 * Each data type can be uploaded independently with full traceability.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsCustomer } from '../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Upload, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  Trash2, Database, Package, AlertTriangle, Building2, Truck,
  FileText, BarChart3, Users, UserCheck, Settings2, Clock, RefreshCw,
  History, ChevronRight, Warehouse, Info, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { API_BASE_URL } from '../lib/api/client';
import type { Site } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { LucideIcon } from 'lucide-react';
import ImportDetailModal, { type DetailMode, type ImportStepLite, type ImportRunLite } from './data-import/ImportDetailModal';
import ImportResultModal from './data-import/ImportResultModal';
import ImportTemplateSection from './data-import/ImportTemplateSection';
import TemplateButton from './data-import/TemplateButton';
import FileDropZone from '../components/common/FileDropZone';

const API_BASE = `${API_BASE_URL}/data-import/sessions`;

interface ImportType {
  key: string;
  step: number;
  name: string;
  shortName: string;
  description: string;
  icon: LucideIcon;
  siteRequired: boolean;
  /** Reference data stored once per company GROUP (falls back to the company
   *  itself for ungrouped customers) — the tile badge names the group, not
   *  the viewed site. */
  groupOwned?: boolean;
  category: 'reference' | 'core' | 'transactional' | 'config';
  /** Step runs a post-import OTIF recalc that the user can defer. */
  supportsOtifRecalc?: boolean;
}

/** Marks a Target Company value as a company group rather than a site. */
const GROUP_PREFIX = 'group:';

// CR-6724: the expected-column list each type used to carry here has moved to
// the backend spec (data_import/column_spec), which the importers validate
// against and the .xlsx template is generated from. Held in two places, the
// lists drifted: four types advertised columns no importer read, and the NCR
// import required a date column that was never shown — so every NCR built from
// this list imported dated the day of the import. One definition now serves
// all three consumers.
const IMPORT_TYPES: ImportType[] = [
  // Ownership follows the selected company: it lands on that company's
  // group when it has one (shared by every site in the group), otherwise on
  // the company itself. Hence siteRequired — the target must be picked at
  // upload time, exactly like GRNs/POs.
  { key: 'commodity', step: 1, name: 'Commodity Groups & Types', shortName: 'Commodities', description: 'Define commodity groups and types for spend categorisation and supplier mapping', icon: Package, siteRequired: true, groupOwned: true, category: 'reference' },
  { key: 'faultcodes', step: 2, name: 'Fault Code Categories', shortName: 'Fault Codes', description: 'Define fault groups and categories used in NCR classification', icon: AlertTriangle, siteRequired: true, groupOwned: true, category: 'reference' },
  { key: 'commonised_ca', step: 12, name: 'Commonised Corrective Actions', shortName: 'Commonised CAs', description: 'Master list of standardised corrective actions selectable on the 8D CAR', icon: Wrench, siteRequired: false, category: 'reference' },
  { key: 'departments', step: 3, name: 'Departments', shortName: 'Departments', description: 'Define departments for user role assignment', icon: Settings2, siteRequired: false, category: 'reference' },
  // Matched on Supplier Code — lands on the supplier company, so it shows on
  // every NCR for that supplier including ERP-imported ones.
  { key: 'supplier_quality_master', step: 13, name: 'Supplier Quality Master Data', shortName: 'Quality Master', description: 'Assign UK/EU status, supplier quality contact and the owning quality engineer against supplier code — shown on every NCR for that supplier', icon: UserCheck, siteRequired: false, category: 'reference' },
  { key: 'warehouses', step: 4, name: 'Warehouses', shortName: 'Warehouses', description: 'Define warehouse/plant locations for stock planning and delivery routing', icon: Warehouse, siteRequired: false, category: 'reference' },
  { key: 'suppliers', step: 5, name: 'Supplier Companies', shortName: 'Suppliers', description: 'Import supplier companies with site assignments, currency, and OTIF window settings', icon: Building2, siteRequired: false, category: 'core' },
  { key: 'supplier_additional', step: 6, name: 'Supplier Additional Details', shortName: 'Supplier Details', description: 'Extended supplier information including address, strategic status, commodity assignments, and KPI inclusion flags', icon: Building2, siteRequired: false, category: 'core' },
  { key: 'grns', step: 7, name: 'Goods Receipt Notes (GRNs)', shortName: 'GRNs', description: 'Historical delivery data — also auto-creates POs and PO lines if they don\'t exist', icon: Truck, siteRequired: true, category: 'transactional', supportsOtifRecalc: true },
  { key: 'pos', step: 8, name: 'Purchase Orders (Open)', shortName: 'POs', description: 'Open purchase orders with line items, quantities, and pricing', icon: FileText, siteRequired: true, category: 'transactional', supportsOtifRecalc: true },
  { key: 'ncrs', step: 9, name: 'Non-Conformance Reports', shortName: 'NCRs', description: 'Quality issues linked to GRNs — includes rejection codes and fault classification', icon: AlertCircle, siteRequired: false, category: 'transactional' },
  { key: 'forecasts', step: 10, name: 'Demand Forecasts', shortName: 'Forecasts', description: 'Future demand data per part and supplier for forecast planning', icon: BarChart3, siteRequired: true, category: 'transactional' },
  { key: 'users', step: 11, name: 'User Accounts', shortName: 'Users', description: 'Create user accounts with site access, module permissions, and notification preferences', icon: Users, siteRequired: false, category: 'config' },
];

const CATEGORIES = [
  { key: 'reference', label: 'Reference Data', description: 'Set up before importing transactional data' },
  { key: 'core', label: 'Supplier Data', description: 'Import supplier companies and their details' },
  { key: 'transactional', label: 'Transactional Data', description: 'POs, deliveries, quality records, and forecasts' },
  { key: 'config', label: 'Configuration', description: 'User accounts and system setup' },
];

/** One upload attempt of one step. Survives the re-upload reset that wipes
 *  ImportStep, so earlier attempts stay readable. */
interface StepRun {
  /** Run id, or null when synthesised from a step that predates run history —
   *  those cannot be expanded because there is no run record to fetch. */
  id: number | null;
  step_number: number;
  status: string;
  site: number | null;
  site_name: string | null;
  file_name: string;
  rows_total: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  /** First 20 reasons. Compare against error_count to see if more exist. */
  errors: string[];
  error_count: number;
  started_by_name: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface StepResult {
  id: number;
  step_number: number;
  status: string;
  site: number | null;
  site_name: string | null;
  file_name: string;
  rows_total: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  errors: string[];
  error_count?: number;
  /** Absent on steps serialised before per-run history existed. */
  runs?: StepRun[];
  completed_at: string | null;
  started_at: string | null;
}

interface Session {
  id: number;
  status: string;
  current_step: number;
  created_by_name: string | null;
  created_at: string;
  steps: StepResult[];
}

// Step numbers whose imported rows are group-owned (the groupOwned tiles):
// results/history matching must treat sibling sites of the same group as the
// same target, or a group-owned import done "for" site A reads as never
// imported when viewing site B of the same group.
const GROUP_OWNED_STEPS = new Set(
  IMPORT_TYPES.filter(t => t.groupOwned).map(t => t.step),
);

/**
 * Runaway guard on the session paging below. At the endpoint's maximum page
 * size of 100 this allows 5,000 sessions — far past any real install, so it
 * only ever trips on a malformed `next` cursor.
 */
const MAX_SESSION_PAGES = 50;

/**
 * Fetch every import session, not just the first page.
 *
 * The endpoint paginates at 20 and caps `page_size` at 100
 * (helix_backend/pagination.py), so asking for a bigger page is not enough —
 * the Import History table and the Errors drill-down only ever saw the 20 most
 * recent sessions, and older runs were unreachable even though their errors
 * were still stored. Follow `next` until it runs out.
 *
 * @param firstUrl - the sessions URL including any view-scope query params
 * @param headers - auth headers to send on every page
 * @returns every session, and whether the page guard cut the walk short
 */
async function fetchAllSessions(
  firstUrl: string, headers: Record<string, string>,
): Promise<{ sessions: Session[]; truncated: boolean }> {
  const collected: Session[] = [];
  let url: string | null = firstUrl;
  let pages = 0;
  while (url && pages < MAX_SESSION_PAGES) {
    const res: Response = await fetch(url, { headers });
    if (!res.ok) break;
    const data = await res.json();
    const page = (data.results || data || []) as Session[];
    if (Array.isArray(page)) collected.push(...page);
    url = (data.next as string | null) || null;
    pages += 1;
  }
  return { sessions: collected, truncated: !!url };
}

export default function DataImport() {
  const isCustomer = useIsCustomer();
  if (!isCustomer) {
    return <Navigate to="/purchasing" replace />;
  }
  return <DataImportContent />;
}

function DataImportContent() {
  const { getViewQueryParams, viewLevel, selectedSiteId: viewSelectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { isDataImportTileEnabled } = useMenuSettings();
  const visibleImportTypes = IMPORT_TYPES.filter(t => isDataImportTileEnabled(t.key));
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [stepResults, setStepResults] = useState<Record<number, StepResult>>({});
  // Every upload attempt for the selected company, newest first — the Errors
  // drill-down reads this so earlier attempts stay reachable after a re-upload.
  const [runHistory, setRunHistory] = useState<StepRun[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadDialog, setUploadDialog] = useState<ImportType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  // GRN/PO imports normally recalculate OTIF inline. On a large historical
  // file that pass dominates the request, so it can be switched off here and
  // triggered afterwards from the tile below.
  const [recalcOtif, setRecalcOtif] = useState(true);
  const [recalcRunning, setRecalcRunning] = useState(false);
  const [otdRecalcRunning, setOtdRecalcRunning] = useState(false);
  // Upload dialog's own target — a site id, or `group:<id>` when the user
  // picks a whole company group. Kept apart from the page-level selector.
  const [uploadTargetId, setUploadTargetId] = useState<string>('');
  // Post-import outcome, shown as a modal rather than a toast so the row
  // counts and any failed rows stay on screen until dismissed.
  const [uploadResult, setUploadResult] = useState<
    { type: ImportType; result: StepResult; targetLabel: string } | null
  >(null);
  // CR-5320 + CR-5321: drill-down modal state. mode controls which view
  // the modal renders; steps + stepFocus narrow the data shown.
  const [detail, setDetail] = useState<{
    mode: DetailMode;
    steps: ImportStepLite[];
    /** Errors mode only — every upload attempt, newest first. */
    runs?: ImportRunLite[];
    stepFocus?: 'created' | 'updated' | 'failed';
    title: string;
    description?: string;
  } | null>(null);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('helix_auth_token') || sessionStorage.getItem('helix_auth_token') || '';
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '';
    return { 'Authorization': `Token ${token}`, 'X-CSRFToken': csrfToken };
  }, []);

  /**
   * Fetch every failure reason for one upload attempt.
   *
   * The session list carries only the first 20 per run so loading the whole
   * history stays cheap; the rest are pulled on demand when a run is expanded.
   *
   * @param runId - id of the ImportStepRun to read
   * @returns the complete list of reasons for that attempt
   */
  const fetchRunErrors = useCallback(async (runId: number): Promise<string[]> => {
    const res = await fetch(`${API_BASE_URL}/data-import/step-runs/${runId}/`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to load errors (${res.status})`);
    const data = await res.json();
    return (data.errors || []) as string[];
  }, [getAuthHeaders]);

  useEffect(() => {
    if (viewContextLoading) return;
    loadInitialData();
  }, [viewContextLoading, viewLevel, viewSelectedSiteId, selectedCompanyId, selectedGroupId]);

  // Recompute step results when site selection changes
  useEffect(() => {
    if (sessions.length > 0 && selectedSiteId) {
      updateStepResultsForSite(sessions, selectedSiteId);
    }
  }, [selectedSiteId, sessions]);

  // CR-5319 round 2: pulse Per-Company cards briefly when the site
  // company changes so users get visible feedback that the per-site
  // tiles re-evaluated. Sarah's complaint was "no change on my side"
  // because most data types are Global by design and don't shift; we
  // animate the cards that *do* shift to draw the eye. Now driven by the
  // top-bar switcher rather than a page-level dropdown.
  const [sitePulseKey, setSitePulseKey] = useState(0);
  const previousSiteRef = useRef<string>('');
  useEffect(() => {
    if (selectedSiteId && previousSiteRef.current && previousSiteRef.current !== selectedSiteId) {
      setSitePulseKey(k => k + 1);
    }
    previousSiteRef.current = selectedSiteId;
  }, [selectedSiteId]);

  const selectedSiteName = sites.find(s => String(s.id) === selectedSiteId)?.name || '';
  // Group of the site being imported for (not the viewer's own group):
  // group-owned reference tiles badge the OWNING group, which follows the
  // upload target exactly as the backend's owner resolution does.
  const selectedSite = sites.find(s => String(s.id) === selectedSiteId);
  const selectedSiteGroupName = selectedSite?.group_id ? selectedSite.group_name : null;
  const selectedSiteGroupId = selectedSite?.group_id ?? null;

  // Distinct company groups across the accessible sites. Empty for tenants
  // with no group structure, which hides the group options entirely.
  const availableGroups = Array.from(
    sites.reduce((acc, s) => {
      if (s.group_id && s.group_name && !acc.has(s.group_id)) {
        acc.set(s.group_id, { id: s.group_id, name: s.group_name });
      }
      return acc;
    }, new Map<number, { id: number; name: string }>()).values()
  );

  // A user with access to exactly one site has nothing to choose between, so
  // the upload dialog states the target instead of offering a one-option
  // dropdown. Checked on sites (not sites + groups): offering "whole group"
  // to someone who can only see one site in it would write beyond their access.
  const onlyOneTarget = sites.length === 1;

  /**
   * Run the OTIF/on-time recalculation for the selected company on demand —
   * the companion to switching it off during a GRN/PO upload.
   */
  const handleRecalculateOtif = async () => {
    if (!selectedSiteId) {
      toast.error('Select a company first');
      return;
    }
    setRecalcRunning(true);
    try {
      const sid = await ensureSession();
      const res = await fetch(`${API_BASE}/${sid}/recalculate-otif/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'OTIF recalculation failed');
        return;
      }
      // Always completed now — the endpoint runs the recalc inline rather
      // than queueing it and claiming success regardless.
      const updated = data.result?.updated;
      toast.success(
        typeof updated === 'number'
          ? `OTIF recalculation complete for ${data.company} — ${updated.toLocaleString()} lines rescored`
          : `OTIF recalculation complete for ${data.company}`
      );
      loadInitialData();
    } catch {
      toast.error('OTIF recalculation failed');
    } finally {
      setRecalcRunning(false);
    }
  };

  /**
   * Rebuild every GRN's cached on_time_status for the selected company.
   *
   * Separate from the OTIF recalc because it is the only path that
   * reconsiders a GRN whose on_time_status is already set — every other
   * caller matches NULL rows only, so a stored verdict that disagrees with
   * the current rule can never be corrected from the UI.
   */
  const handleRecalculateOtd = async () => {
    if (!selectedSiteId) {
      toast.error('Select a company first');
      return;
    }
    setOtdRecalcRunning(true);
    try {
      const sid = await ensureSession();
      const res = await fetch(`${API_BASE}/${sid}/recalculate-otd/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'OTD recalculation failed');
        return;
      }
      const updated = data.result?.updated;
      toast.success(
        typeof updated === 'number'
          ? `OTD recalculation complete for ${data.company} — ${updated.toLocaleString()} deliveries rescored`
          : `OTD recalculation complete for ${data.company}`
      );
      loadInitialData();
    } catch {
      toast.error('OTD recalculation failed');
    } finally {
      setOtdRecalcRunning(false);
    }
  };

  const updateStepResultsForSite = (allSessions: Session[], siteId: string, sitesArr: Site[] = sites) => {
    // Build the latest completed step result per step_number for the selected company
    const resultMap: Record<number, StepResult> = {};
    const siteIdNum = parseInt(siteId, 10);
    const groupOf = (id: number | null | undefined) =>
      id ? sitesArr.find(s => s.id === id)?.group_id ?? null : null;
    const selectedGroup = groupOf(siteIdNum);

    // Flatten all steps across sessions, filter by site, pick latest per step_number
    const allSteps: (StepResult & { sessionId: number })[] = [];
    for (const sess of allSessions) {
      for (const step of sess.steps || []) {
        if (step.status === 'pending') continue;
        allSteps.push({ ...step, sessionId: sess.id });
      }
    }

    // For each step_number, find the latest completed step matching this site (or no site for global imports)
    for (const step of allSteps) {
      // Match: no site on the step (global import), the selected site itself,
      // or — for group-owned steps — any sibling site in the selected site's
      // group (the rows land on the group, so they belong to every member).
      const sameGroup = GROUP_OWNED_STEPS.has(step.step_number) &&
        selectedGroup !== null && groupOf(step.site) === selectedGroup;
      const matches = !step.site || step.site === siteIdNum || sameGroup;
      if (!matches) continue;

      const existing = resultMap[step.step_number];
      if (!existing || (step.completed_at && existing.completed_at && new Date(step.completed_at) > new Date(existing.completed_at))) {
        resultMap[step.step_number] = step;
      }
    }
    setStepResults(resultMap);

    // Every upload attempt, not just the latest of each type. The tiles and
    // summary cards want the latest; the Errors drill-down needs the earlier
    // ones too, since re-uploading a corrected file resets the step and used
    // to put the previous attempt's reasons permanently out of reach.
    const runs: StepRun[] = [];
    for (const step of allSteps) {
      if (step.runs && step.runs.length > 0) {
        for (const run of step.runs) {
          runs.push({ ...run, step_number: step.step_number, site: run.site ?? step.site });
        }
      } else {
        // Steps recorded before run history existed have no runs. Surface the
        // step itself as a single attempt so nothing already on screen
        // disappears; id null marks it as not expandable.
        runs.push({
          id: null,
          step_number: step.step_number,
          status: step.status,
          site: step.site,
          site_name: step.site_name,
          file_name: step.file_name,
          rows_total: step.rows_total,
          rows_created: step.rows_created,
          rows_updated: step.rows_updated,
          rows_skipped: step.rows_skipped,
          rows_failed: step.rows_failed,
          errors: step.errors || [],
          error_count: step.error_count ?? (step.errors || []).length,
          started_by_name: null,
          started_at: step.started_at,
          completed_at: step.completed_at,
        });
      }
    }
    const siteRuns = runs
      .filter(run => !run.site || run.site === siteIdNum)
      .sort((a, b) => {
        const at = a.completed_at ? new Date(a.completed_at).getTime() : 0;
        const bt = b.completed_at ? new Date(b.completed_at).getTime() : 0;
        return bt - at;
      });
    setRunHistory(siteRuns);

    // Find or create session for this site
    const active = allSessions.find((s: Session) => s.status === 'in_progress');
    if (active) {
      setSessionId(active.id);
    }
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load sites
      let loadedSiteId = '';
      let loadedSites: Site[] = [];
      try {
        const sitesRes = await coreAPI.getAccessibleSites();
        const sitesData = Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || [];
        loadedSites = sitesData;
        setSites(sitesData);
        // The top-bar company switcher is the single source of truth for
        // "which company am I looking at" — this page follows it rather than
        // carrying its own selector. Previously it defaulted to sitesData[0],
        // so the header could name one company while every tile below named
        // whichever site happened to be first in the accessible list.
        // Company IS Site here, so a COMPANY-level view populates
        // selectedSiteId too; only a GROUP-level view leaves it null, and
        // there we fall back to the first accessible site.
        if (sitesData.length > 0) {
          const viewSite = viewSelectedSiteId ?? selectedCompanyId;
          const viewMatches = viewSite != null && sitesData.some(s => s.id === viewSite);
          loadedSiteId = viewMatches ? String(viewSite) : String(sitesData[0].id);
          if (loadedSiteId !== selectedSiteId) {
            setSelectedSiteId(loadedSiteId);
          }
        }
      } catch { /* optional */ }

      // Load all sessions for history, scoped by view context
      const viewParams = getViewQueryParams();
      const viewQuery = Object.entries(viewParams)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      // page_size=100 is the endpoint's maximum; fetchAllSessions walks
      // `next` from there so history is not cut off at the first page.
      const params = [viewQuery, 'page_size=100'].filter(Boolean).join('&');
      const sessionUrl = `${API_BASE}/?${params}`;
      const { sessions: results, truncated } = await fetchAllSessions(sessionUrl, getAuthHeaders());
      if (truncated) {
        console.warn(`Import history stopped after ${MAX_SESSION_PAGES} pages; older sessions were not loaded.`);
      }
      setSessions(results);

      // Populate step results for selected site
      if (loadedSiteId) {
        updateStepResultsForSite(results, loadedSiteId, loadedSites);
      }
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const ensureSession = async (): Promise<number> => {
    if (sessionId) return sessionId;
    const res = await fetch(`${API_BASE}/`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error('Failed to create session');
    const data = await res.json();
    setSessionId(data.id);
    return data.id;
  };

  const handleUploadClick = (importType: ImportType) => {
    setUploadDialog(importType);
    setSelectedFile(null);
    // Dialog target starts from the page selector but is tracked separately,
    // so picking a group here doesn't repoint the whole page.
    setUploadTargetId(selectedSiteId);
    setRecalcOtif(true);
  };

  // SI-2: after a network-cut upload, ask the server what actually
  // happened before reporting anything. The platform router abandons
  // responses at ~30 s while the import keeps running server-side and
  // usually COMMITS — the old blanket 'Upload failed' toast reported
  // success as failure and sent operators into re-upload loops.
  const computeTargetLabel = (targetId: string, groupOwned?: boolean) => {
    const groupPick = targetId.startsWith(GROUP_PREFIX)
      ? availableGroups.find(g => `${GROUP_PREFIX}${g.id}` === targetId)
      : undefined;
    if (groupPick) return `${groupPick.name} (whole group)`;
    const targetSite = sites.find(s => String(s.id) === targetId);
    // Group-owned rows land on the target's GROUP — announce the group, not
    // the site picked in the dialog.
    if (groupOwned && targetSite?.group_id && targetSite.group_name) {
      return `${targetSite.group_name} (group)`;
    }
    return targetSite?.name || '';
  };

  // priorStamp identifies THIS SESSION's step record as the server held it
  // before this upload attempt: if the request never reached the server the
  // record keeps its previous timestamps, and reporting it as this attempt's
  // outcome would be a false success/failure. The baseline must come from
  // the session's own record — never the site-filtered stepResults view,
  // where a sibling target's completed record hides the session's own.
  const stepStamp = (st: StepResult | undefined | null) =>
    st ? `${st.status}|${st.started_at}|${st.completed_at}` : null;

  const pollStepOutcome = async (
    sid: number, stepNumber: number, priorStamp: string | null,
  ): Promise<{ step: StepResult | null; observed: boolean }> => {
    // observed: the server was SEEN acting on this attempt (the step's
    // stamp moved off the pre-upload baseline) — the only licence for a
    // "still processing" claim if the budget runs out.
    let observed = false;
    for (let attempt = 0; attempt < 12; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const res = await fetch(`${API_BASE}/${sid}/`, { headers: getAuthHeaders() });
        if (!res.ok) continue;
        const session: Session = await res.json();
        const step = (session.steps || []).find(st => st.step_number === stepNumber);
        if (!step || stepStamp(step) === priorStamp) continue;
        observed = true;
        if (step.status === 'completed' || step.status === 'failed') return { step, observed };
      } catch {
        // Transient poll failure — keep waiting; the ~60 s budget bounds us.
      }
    }
    return { step: null, observed };
  };

  const handleUpload = async () => {
    if (!uploadDialog || !selectedFile) return;
    setUploading(uploadDialog.step);

    let sid: number | null = null;
    let priorStamp: string | null = null;
    let baselineKnown = false;
    try {
      sid = await ensureSession();
      try {
        const baseRes = await fetch(`${API_BASE}/${sid}/`, { headers: getAuthHeaders() });
        if (baseRes.ok) {
          const baseSession: Session = await baseRes.json();
          priorStamp = stepStamp(
            (baseSession.steps || []).find(st => st.step_number === uploadDialog.step),
          );
          baselineKnown = true;
        }
      } catch {
        // Baseline unavailable — the catch skips polling: without it a
        // terminal record cannot be told apart from a stale one.
      }
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (uploadDialog.siteRequired && uploadTargetId) {
        if (uploadTargetId.startsWith(GROUP_PREFIX)) {
          formData.append('company_group_id', uploadTargetId.slice(GROUP_PREFIX.length));
        } else {
          formData.append('site_id', uploadTargetId);
        }
      }
      if (uploadDialog.supportsOtifRecalc && !recalcOtif) {
        formData.append('recalc_otif', 'false');
      }

      const res = await fetch(`${API_BASE}/${sid}/upload/${uploadDialog.step}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        setStepResults(prev => ({ ...prev, [uploadDialog.step]: data }));
        setUploadResult({ type: uploadDialog, result: data, targetLabel: computeTargetLabel(uploadTargetId, uploadDialog.groupOwned) });
        setUploadDialog(null);
        setSelectedFile(null);
        loadInitialData(); // Refresh history
      } else {
        toast.error(data.error || 'Import failed');
        if (data.step) setStepResults(prev => ({ ...prev, [uploadDialog.step]: data.step }));
      }
    } catch {
      if (sid === null) {
        // Failed before an import session existed — nothing is running
        // server-side, so a plain failure message is the truth here.
        toast.error('Upload failed — could not reach the server.');
      } else {
        if (baselineKnown) {
          toast.info('The connection dropped while the server was processing — checking what actually happened…', { duration: 10000 });
        }
        const { step: outcome, observed } = baselineKnown
          ? await pollStepOutcome(sid, uploadDialog.step, priorStamp)
          : { step: null, observed: false };
        if (outcome?.status === 'completed') {
          // The import finished and committed despite the dropped response.
          setStepResults(prev => ({ ...prev, [uploadDialog.step]: outcome }));
          setUploadResult({ type: uploadDialog, result: outcome, targetLabel: computeTargetLabel(uploadTargetId) });
          setUploadDialog(null);
          setSelectedFile(null);
          loadInitialData();
        } else if (outcome?.status === 'failed') {
          const detail = (outcome.errors || []).slice(0, 3).join('; ');
          toast.error(detail ? `Import failed: ${detail}` : 'Import failed on the server — see Import History for details.');
          setStepResults(prev => ({ ...prev, [uploadDialog.step]: outcome }));
        } else if (observed) {
          // The server was seen working this attempt but didn't finish
          // inside our polling budget: the CONNECTION was cut, not the
          // import. Never invite a re-upload here.
          setUploadDialog(null);
          setSelectedFile(null);
          toast.info(
            'The import is still processing on the server. Check Import History in a few minutes — do not re-upload the same file.',
            { duration: 12000 },
          );
          loadInitialData();
        } else {
          // No evidence the upload ever reached the server — say exactly
          // that, and leave the dialog open so a retry is easy once the
          // connection is back.
          toast.error(
            'Could not confirm the upload reached the server. Check Import History before re-uploading the same file.',
            { duration: 12000 },
          );
          loadInitialData();
        }
      }
    } finally {
      setUploading(null);
    }
  };

  // Stats
  const totalImports = Object.values(stepResults).filter(r => r.status === 'completed').length;
  const totalRowsCreated = Object.values(stepResults).reduce((sum, r) => sum + (r.rows_created || 0), 0);
  const totalErrors = Object.values(stepResults).reduce((sum, r) => sum + (r.rows_failed || 0), 0);
  const lastImport = Object.values(stepResults)
    .filter(r => r.completed_at)
    .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Import</h1>
          <p className="text-muted-foreground">Upload Excel files to set up and manage your platform data</p>
        </div>
        <div className="flex items-center gap-3">
          {/* No page-level company selector: the page follows the top-bar
              company switcher, and the target for an individual file is
              chosen in that tile's upload dialog. Two competing selectors
              meant the header and the tiles could disagree. */}
          {/* Companion to the upload dialog's "Recalculate OTIF" opt-out:
              land a big GRN/PO file fast, then refresh delivery metrics
              here once the rows are in. */}
          <Button
            variant="outline"
            onClick={handleRecalculateOtif}
            disabled={recalcRunning || !selectedSiteId}
            title={selectedSiteName ? `Recalculate OTIF for ${selectedSiteName}` : 'Select a company first'}
          >
            {recalcRunning
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-2" />}
            Recalculate OTIF
          </Button>
          {/* OTD is cached per GRN and written once at import. Nothing else
              revisits a row that already has a value, so this is the only
              way to rebuild it after a rule or date change. */}
          <Button
            variant="outline"
            onClick={handleRecalculateOtd}
            disabled={otdRecalcRunning || !selectedSiteId}
            title={selectedSiteName ? `Recalculate OTD for ${selectedSiteName}` : 'Select a company first'}
          >
            {otdRecalcRunning
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-2" />}
            Recalculate OTD
          </Button>
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)}>
            <History className="h-4 w-4 mr-2" />
            {showHistory ? 'Hide History' : 'Import History'}
          </Button>
          <Button variant="outline" size="icon" onClick={loadInitialData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* CR-5319 round 2: explicit banner naming which tiles are company-
          scoped. Without this, switching company looks like nothing happened
          — most data types are Global by design and stay identical across
          sites. Reworded now that the page follows the top-bar switcher
          instead of its own "Importing for" dropdown. */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p>
            <span className="font-semibold">Per-Company</span> tiles
            (Commodities, Fault Codes, GRNs, Purchase Orders, Forecasts) and the
            {' '}<span className="font-semibold">Last Import</span> card
            {onlyOneTarget ? ' apply to ' : ' follow the company you’re viewing'}
            {selectedSiteName ? (
              onlyOneTarget
                ? <span className="font-semibold">{selectedSiteName}</span>
                : <> — currently <span className="font-semibold">{selectedSiteName}</span></>
            ) : null}
            {/* A user with access to a single company has no switcher to
                change and nowhere else to send a file — promising either
                would send them hunting for a control that isn't there. */}
            {onlyOneTarget ? '.' : ', which you change from the company switcher at the top right.'}
            {' '}<span className="font-semibold">Global</span> tiles
            (Departments, Suppliers, NCRs, Users)
            apply across all companies{onlyOneTarget ? '.' : ' and stay the same when you switch.'}
            {!onlyOneTarget && (
              <>
                {' '}You can still send an individual file somewhere else by changing
                {' '}<span className="font-semibold">Target Company</span> in that tile's upload dialog.
              </>
            )}
            {' '}Commodities and Fault Codes land on the target company's
            {' '}<span className="font-semibold">group</span> when it belongs to one,
            so every site in that group shares them.
          </p>
        </div>
      </div>

      {/* Summary Stats — CR-5321: top three cards are now drill-down
          triggers. The fourth (Last Import) is informational only. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          type="button"
          className="text-left"
          disabled={totalImports === 0}
          onClick={() => setDetail({
            mode: 'types',
            steps: Object.values(stepResults).filter(s => s.status === 'completed'),
            title: 'Data types imported',
            description: 'Latest completed import per data type for the selected company.',
          })}
        >
          <Card className={totalImports > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Data Types Imported</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalImports}</div>
              <p className="text-xs text-muted-foreground">of {visibleImportTypes.length} available</p>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          className="text-left"
          disabled={totalRowsCreated === 0}
          onClick={() => setDetail({
            mode: 'created',
            steps: Object.values(stepResults).filter(s => s.status === 'completed'),
            title: 'Records created',
            description: 'Per-import breakdown of created rows.',
          })}
        >
          <Card className={totalRowsCreated > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Records Created</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{totalRowsCreated.toLocaleString()}</div>
              {/* CR-5319: clarify scope so users don't read "across all
                  imports" as "every site". Globals are always counted; the
                  per-company portion is what changes with the company. */}
              <p className="text-xs text-muted-foreground">current company + global</p>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          className="text-left"
          disabled={totalErrors === 0}
          onClick={() => setDetail({
            mode: 'errors',
            steps: [],
            // Every attempt, not just the latest of each type — the modal
            // splits it into current and previous itself. The card's number
            // stays the latest-run count above, which is the one you act on;
            // an all-time total would only ever climb.
            runs: runHistory.filter(r => r.status !== 'pending'),
            title: 'Import errors',
            description: 'Rows that failed to import, grouped by data type.',
          })}
        >
          <Card className={totalErrors > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Errors</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{totalErrors}</div>
              <p className="text-xs text-muted-foreground">rows with issues</p>
            </CardContent>
          </Card>
        </button>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Last Import</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lastImport?.completed_at ? formatDistanceToNow(new Date(lastImport.completed_at), { addSuffix: true }) : '—'}
            </div>
            <p className="text-xs text-muted-foreground">{lastImport?.file_name || 'No imports yet'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Import History Table */}
      {showHistory && sessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Import History
            </CardTitle>
            <CardDescription>All import sessions with who uploaded what and when</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data Type</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // CR-5319: sort History table rows newest-first so users
                  // can spot recent imports at a glance. Previously the
                  // rows came out in whatever order the API returned the
                  // sessions/steps, which produced nonsense like "19 days
                  // ago" appearing above "24 minutes ago". Also tag
                  // global (site-less) steps with a "Global" badge so the
                  // user can see *why* a row stays visible after they
                  // change the site selector.
                  const siteIdNum = selectedSiteId ? parseInt(selectedSiteId, 10) : null;
                  const allRows: Array<{ sess: Session; step: StepResult }> = [];
                  for (const sess of sessions) {
                    for (const step of sess.steps || []) {
                      if (step.status === 'pending') continue;
                      // Group-owned steps belong to every site in the target's
                      // group — keep sibling-site rows visible (same rule as
                      // the tile results above).
                      const stepGroup = step.site ? (sites.find(x => x.id === step.site)?.group_id ?? null) : null;
                      const sameGroup = GROUP_OWNED_STEPS.has(step.step_number) &&
                        selectedSiteGroupId !== null && stepGroup === selectedSiteGroupId;
                      if (siteIdNum !== null && step.site && step.site !== siteIdNum && !sameGroup) continue;
                      allRows.push({ sess, step });
                    }
                  }
                  allRows.sort((a, b) => {
                    // Site-scoped rows for the current site first, globals
                    // after. Within each group sort by completed_at desc.
                    const aGlobal = !a.step.site;
                    const bGlobal = !b.step.site;
                    if (aGlobal !== bGlobal) return aGlobal ? 1 : -1;
                    const at = a.step.completed_at ? new Date(a.step.completed_at).getTime() : 0;
                    const bt = b.step.completed_at ? new Date(b.step.completed_at).getTime() : 0;
                    return bt - at;
                  });
                  return allRows.map(({ sess, step }) => {
                    const importType = IMPORT_TYPES.find(t => t.step === step.step_number);
                    const isGlobal = !step.site;
                    return (
                      <TableRow key={`${sess.id}-${step.step_number}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {importType && <importType.icon className="h-4 w-4 text-muted-foreground" />}
                              <span className="font-medium text-sm">{importType?.shortName || `Step ${step.step_number}`}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {isGlobal ? (
                              <Badge variant="outline" className="text-[10px]">Global</Badge>
                            ) : (
                              step.site_name || <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{step.file_name || '—'}</TableCell>
                          <TableCell>
                            <Badge className={
                              step.status === 'completed' && step.rows_failed > 0 ? 'bg-amber-100 text-amber-800' :
                              step.status === 'completed' ? 'bg-green-100 text-green-800' :
                              step.status === 'failed' ? 'bg-red-100 text-red-800' :
                              step.status === 'skipped' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {step.status === 'completed' && step.rows_failed > 0 ? 'partial' : step.status}
                            </Badge>
                          </TableCell>
                          {/* CR-5320: Created / Updated / Failed cells
                              are drill-down triggers. Disabled when zero
                              so empty cells don't look interactive. */}
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_created || 0) === 0}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'created',
                                title: `${importType?.shortName || 'Step'} — Created records`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm font-medium ${(step.rows_created || 0) > 0 ? 'text-green-700 hover:underline cursor-pointer' : 'text-green-700 cursor-default'}`}
                            >
                              {step.rows_created || 0}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_updated || 0) === 0}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'updated',
                                title: `${importType?.shortName || 'Step'} — Updated records`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm ${(step.rows_updated || 0) > 0 ? 'text-blue-700 hover:underline cursor-pointer' : 'text-blue-700 cursor-default'}`}
                            >
                              {step.rows_updated || 0}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_failed || 0) === 0 && (!step.errors || step.errors.length === 0)}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'failed',
                                title: `${importType?.shortName || 'Step'} — Failed rows`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm ${(step.rows_failed || 0) > 0 ? 'text-red-700 hover:underline cursor-pointer font-semibold' : 'text-red-700 cursor-default'}`}
                            >
                              {step.rows_failed || 0}
                            </button>
                          </TableCell>
                          <TableCell className="text-sm">{sess.created_by_name || 'System'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {step.completed_at ? formatDistanceToNow(new Date(step.completed_at), { addSuffix: true }) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    });
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Import Type Cards - Grouped by Category */}
      {CATEGORIES.map(cat => {
        const types = visibleImportTypes.filter(t => t.category === cat.key);
        if (types.length === 0) return null;
        return (
          <div key={cat.key}>
            <div className="mb-3">
              <h2 className="text-lg font-semibold">{cat.label}</h2>
              <p className="text-sm text-muted-foreground">{cat.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {types.map(type => {
                const result = stepResults[type.step];
                const Icon = type.icon;
                const hasResult = result && result.status !== 'pending';
                const hasErrors = hasResult && result.rows_failed > 0;
                const isSuccess = hasResult && result.status === 'completed' && !hasErrors;
                const isWarning = hasResult && result.status === 'completed' && hasErrors;
                const isFailed = hasResult && result.status === 'failed';

                // CR-5319 round 2: per-company cards get a stronger left
                // border + a brief flash on site change so the impact of
                // the dropdown is unmistakable. The flash is keyed on
                // sitePulseKey + type.key so React remounts the card
                // each time the site changes, restarting the animation.
                const perCompany = type.siteRequired;
                const cardClass = perCompany
                  ? 'group hover:shadow-md transition-all cursor-pointer border-l-4 border-l-primary'
                  : 'group hover:shadow-md hover:border-primary/30 transition-all cursor-pointer';
                const pulseClass = perCompany ? 'animate-in fade-in zoom-in-95 duration-300' : '';
                return (
                  <Card
                    key={`${type.key}-${perCompany ? sitePulseKey : 'g'}`}
                    className={`${cardClass} ${pulseClass}`}
                    onClick={() => handleUploadClick(type)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-lg ${
                            isSuccess ? 'bg-green-100' :
                            isWarning ? 'bg-amber-100' :
                            isFailed ? 'bg-red-100' :
                            'bg-muted'
                          }`}>
                            <Icon className={`h-5 w-5 ${
                              isSuccess ? 'text-green-700' :
                              isWarning ? 'text-amber-700' :
                              isFailed ? 'text-red-700' :
                              'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{type.name}</h3>
                            {/* CR-5319: tag every card so users can tell at a
                                glance which counts respond to the "Importing
                                for" dropdown and which don't. Without the
                                Global badge, switching sites looked like
                                "nothing changed" because most data types are
                                site-less and stayed identical. Group-owned
                                reference data is stored once per company
                                GROUP, so naming the viewed site there read as
                                per-site data — badge the viewed site's group
                                instead (ungrouped companies keep the site
                                badge: ownership falls back to the company). */}
                            <Badge
                              variant={type.siteRequired ? 'default' : 'outline'}
                              className={`text-[9px] px-1 py-0 mt-0.5 ${
                                type.siteRequired ? '' : 'text-muted-foreground'
                              }`}
                            >
                              {type.siteRequired
                                ? (type.groupOwned && selectedSiteGroupName
                                    ? `For ${selectedSiteGroupName} (group)`
                                    : (selectedSiteName ? `For ${selectedSiteName}` : 'Per Company'))
                                : 'Global'}
                            </Badge>
                          </div>
                        </div>
                        {isSuccess && (
                          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                        )}
                        {isWarning && (
                          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                        )}
                        {isFailed && (
                          <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
                        )}
                        {!hasResult && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{type.description}</p>

                      {hasResult ? (
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex gap-3">
                            <span className="text-green-700 font-medium">{result.rows_created} created</span>
                            {result.rows_updated > 0 && <span className="text-blue-700">{result.rows_updated} updated</span>}
                            {result.rows_failed > 0 && <span className="text-red-700">{result.rows_failed} failed</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <TemplateButton type={type} />
                            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={(e) => { e.stopPropagation(); handleUploadClick(type); }}>
                              Re-upload
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // No empty-state line: an Upload button with no counts
                        // beside it already reads as "nothing here yet", and
                        // the per-company variant only repeated the company
                        // named in the badge above.
                        <div className="flex items-center justify-end gap-1">
                          <TemplateButton type={type} />
                          <Button variant="outline" size="sm" className="h-7 text-xs px-3" onClick={(e) => { e.stopPropagation(); handleUploadClick(type); }}>
                            <Upload className="h-3 w-3 mr-1.5" />Upload
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* CR-5320 + CR-5321: drill-down modal */}
      <ImportDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        mode={detail?.mode || 'types'}
        steps={detail?.steps || []}
        runs={detail?.runs || []}
        fetchRunErrors={fetchRunErrors}
        stepFocus={detail?.stepFocus}
        importTypes={IMPORT_TYPES.map(t => ({ step: t.step, shortName: t.shortName, name: t.name }))}
        title={detail?.title || ''}
        description={detail?.description}
      />

      {/* Import outcome — a modal rather than a toast so the counts and any
          failed rows stay on screen until the user dismisses them. */}
      <ImportResultModal
        open={!!uploadResult}
        typeName={uploadResult?.type.name || ''}
        targetLabel={uploadResult?.targetLabel || ''}
        result={uploadResult?.result || null}
        onClose={() => setUploadResult(null)}
        onReupload={() => {
          const t = uploadResult?.type;
          setUploadResult(null);
          if (t) handleUploadClick(t);
        }}
      />

      {/* Upload Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(open) => { if (!open) { setUploadDialog(null); setSelectedFile(null); } }}>
        <DialogContent className="max-w-xl">
          {uploadDialog && (() => {
            const Icon = uploadDialog.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    Import {uploadDialog.name}
                  </DialogTitle>
                  <DialogDescription>{uploadDialog.description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-2">
                  {/* CR-6724: columns and the downloadable template both come
                      from the backend spec the importers validate against.
                      This panel used to render a hand-typed array further up
                      this file, which had drifted from what the importers
                      read — see ImportTemplateSection. */}
                  <ImportTemplateSection
                    step={uploadDialog.step}
                    typeName={uploadDialog.name}
                  />

                  {/* Target selector — company groups first, then individual
                      sites. Defaults to the company the page is showing (the
                      top-bar switcher), so the common case is "just upload".
                      A user with access to a single site has nothing to
                      choose between, so they get a plain read-only line
                      instead of a one-option dropdown — and no "whole group"
                      option, which would otherwise write to sites outside
                      their access. */}
                  {uploadDialog.siteRequired && sites.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">
                        Target Company{!onlyOneTarget && <span className="text-red-500"> *</span>}
                      </label>
                      {onlyOneTarget ? (
                        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium">{sites[0].name}</span>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground">
                            {uploadTargetId.startsWith(GROUP_PREFIX)
                              ? 'This data will be shared by every site in the selected group'
                              : (uploadDialog.groupOwned && (() => {
                                  const t = sites.find(s => String(s.id) === uploadTargetId);
                                  return t?.group_id ? t.group_name : null;
                                })())
                                ? `This data is stored once for ${sites.find(s => String(s.id) === uploadTargetId)?.group_name} — every site in the group will share it`
                                : 'This data will be imported for the selected company'}
                          </p>
                          <Select value={uploadTargetId} onValueChange={setUploadTargetId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select company..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableGroups.map(g => (
                                <SelectItem key={`g${g.id}`} value={`${GROUP_PREFIX}${g.id}`}>
                                  {g.name} (whole group)
                                </SelectItem>
                              ))}
                              {sites.map(site => (
                                <SelectItem key={site.id} value={String(site.id)}>{site.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                    </div>
                  )}

                  {/* Global data types have no target to pick. Say so
                      explicitly — an absent selector reads as a missing
                      control, and the user is left wondering which company
                      the file is about to land on. */}
                  {!uploadDialog.siteRequired && (
                    <div className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2">
                      <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">Applies across all companies</p>
                        <p className="text-xs text-muted-foreground">
                          {uploadDialog.shortName} is a <span className="font-medium">Global</span> data type —
                          {' '}one set shared by every company, so there's no Target Company to choose.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* OTIF recalc opt-out — the recalc walks every touched PO
                      line, so on a big historical file it dwarfs the import
                      itself and risks a gateway timeout. */}
                  {uploadDialog.supportsOtifRecalc && (
                    <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
                      <input
                        id="recalc-otif"
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                        checked={recalcOtif}
                        onChange={e => setRecalcOtif(e.target.checked)}
                      />
                      <div className="space-y-0.5">
                        <label htmlFor="recalc-otif" className="text-sm font-medium cursor-pointer">
                          Recalculate OTIF after import
                        </label>
                        <p className="text-xs text-muted-foreground">
                          {recalcOtif
                            ? 'Leave on for routine files. On a large historical file this pass can take far longer than the import and may time out.'
                            : 'Off — the import will finish faster. Run "Recalculate OTIF" on the page afterwards to refresh the delivery metrics.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Customer review 11 Sep (drag and drop everywhere): shared FileDropZone. */}
                  <FileDropZone
                    accept=".xlsx,.xls,.csv"
                    icon={FileSpreadsheet}
                    hint="Supports .xlsx, .xls, and .csv files"
                    onFiles={(files) => setSelectedFile(files[0])}
                    className="rounded-xl p-8"
                    data-testid="data-import-drop-zone"
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-3 py-2" data-testid="data-import-selected-file">
                      <FileSpreadsheet className="h-5 w-5 text-green-600" />
                      <span className="font-semibold">{selectedFile.name}</span>
                      <span className="text-sm text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                      <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setSelectedFile(null)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove file
                      </Button>
                    </div>
                  )}

                  {/* Previous errors */}
                  {stepResults[uploadDialog.step]?.errors?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                        {/* error_count is the real total; `errors` is only the
                            preview the session list carries, so counting it
                            would under-report a file with many rejects. */}
                        Previous import had {stepResults[uploadDialog.step].error_count ?? stepResults[uploadDialog.step].errors.length} error(s):
                      </p>
                      <div className="text-xs text-amber-700 max-h-24 overflow-y-auto space-y-0.5">
                        {stepResults[uploadDialog.step].errors.slice(0, 5).map((err, i) => (
                          <p key={i}>• {err}</p>
                        ))}
                        {(stepResults[uploadDialog.step].error_count ?? stepResults[uploadDialog.step].errors.length) > 5 && (
                          <p className="font-medium">...and {(stepResults[uploadDialog.step].error_count ?? stepResults[uploadDialog.step].errors.length) - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => { setUploadDialog(null); setSelectedFile(null); }}>Cancel</Button>
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading !== null || (uploadDialog.siteRequired && !uploadTargetId)}
                    className="min-w-[140px]"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" />Upload & Import</>
                    )}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
